import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { subscribeNewsletter } from '../utils/api';
import { useToast } from '../hooks/use-toast';

const Newsletter = () => {
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const { toast } = useToast();
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (email) {
      try {
        await subscribeNewsletter(email);
        toast({
          title: "Subscribed!",
          description: "Thank you for subscribing to our newsletter.",
        });
        setEmail('');
      } catch (err) {
        toast({
          title: "Error",
          description: "Subscription failed. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <section ref={ref} className="py-20 bg-gradient-to-br from-blue-900 via-blue-950 to-blue-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto text-center"
        >
          {/* Icon */}
          <motion.div
            className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl"
            animate={{
              y: [0, -10, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Mail className="h-10 w-10 text-white" />
          </motion.div>

          {/* Heading */}
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
            न्यूज़लेटर सब्सक्राइब करें
          </h2>
          <p 
            className="text-xl text-orange-300 font-semibold mb-4"
            style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
          >
            जुड़े रहें हमारे साथ
          </p>
          <p className="text-gray-300 mb-8" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
            नई पुस्तकों, लेखों, कार्यक्रमों और राष्ट्र निर्माण से जुड़े विचारों की जानकारी पाएं
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input 
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="अपना ईमेल पता दर्ज करें"
              required
              style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
              className="flex-1 h-14 px-6 bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder:text-gray-400 focus:bg-white/20"
            />
            <Button 
              type="submit"
              size="lg"
              style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
              className="h-14 px-8 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:scale-105 transition-all duration-300"
            >
              <Send className="mr-2 h-5 w-5" />
              सब्सक्राइब करें
            </Button>
          </form>

          {/* Privacy Note */}
          <p className="text-sm text-gray-400 mt-6" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
            हम आपकी निजता का सम्मान करते हैं। कभी भी सदस्यता समाप्त कर सकते हैं।
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;